import "mapbox-gl/dist/mapbox-gl.css";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { useInView } from "react-cool-inview";
const Map = dynamic(() => import("react-map-gl"), { ssr: false });
const FullscreenControl = dynamic(
  () => import("react-map-gl").then((mod) => mod.FullscreenControl),
  { ssr: false }
);
const GeolocateControl = dynamic(
  () => import("react-map-gl").then((mod) => mod.GeolocateControl),
  { ssr: false }
);
const NavigationControl = dynamic(
  () => import("react-map-gl").then((mod) => mod.NavigationControl),
  { ssr: false }
);
const AttributionControl = dynamic(
  () => import("react-map-gl").then((mod) => mod.AttributionControl),
  { ssr: false }
);
const MapMarker = dynamic(() => import("components/maps/MapMarker/MapMarker"), {
  ssr: false,
});
const ViatorPopup = dynamic(
  () => import("components/maps/popups/ViatorPopup/ViatorPopup"),
  { ssr: false }
);

import styles from "./GuideMap.module.scss";

import { calculateZoomFromBoundingBox } from "utils/map/coordinates";

export default function GuideMap({
  initialPosition,
  zoom,
  location,
  itineraryItems,
  viatorExperiences,
}) {
  const [activeMarker, setActiveMarker] = useState(null);
  const [selectedViatorProduct, setSelectedViatorProduct] = useState(null);
  const [currentMapMarkers, setCurrentMapMarkers] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  let initialZoom = 12;

  const { observe, inView } = useInView({ unobserveOnEnter: true });

  useEffect(() => {
    const usedCoordinates = {};

    console.log(itineraryItems);

    if (!itineraryItems || !viatorExperiences || !inView) {
      return;
    }

    // filter out activities with duplicate viator product codes
    const uniqueActivities = [
      ...Object.values(itineraryItems),
      ...viatorExperiences,
    ].filter(
      (item, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            t?.viatorActivity?.productCode === item?.viatorActivity?.productCode
        )
    );

    const mapActivities = uniqueActivities
      .map((item) => {
        const destinations = item?.viatorActivity?.destinations ?? [];

        if (!destinations) {
          return null;
        }

        const geocodeInfo = destinations[0]?.geocodeInfo ?? [];

        if (!geocodeInfo) {
          return null;
        }

        const coordinates = geocodeInfo[0]?.geometry?.coordinates ?? [];

        if (!coordinates[0] || !coordinates[1]) {
          return null;
        }

        const coordinatesString = `${coordinates[0]},${coordinates[1]}`;

        // If the coordinates have been used before, offset the marker
        let markerOffset = [0, 0];

        if (usedCoordinates[coordinatesString]) {
          const usedCount = usedCoordinates[coordinatesString] ?? 0;

          const angle = usedCount * 2 * Math.PI;
          const offsetX = usedCount > 0 ? 25 * Math.cos(angle + 5) : 0;
          const offsetY = usedCount > 0 ? 25 * Math.sin(angle + 5) : 0;

          markerOffset = [offsetX, offsetY];

          // Increment the number of times the coordinates have been used to offset the marker by different amounts
          usedCoordinates[coordinatesString] = usedCount + 1;
        }

        if (!usedCoordinates[coordinatesString]) {
          usedCoordinates[coordinatesString] = 1;
        }

        const mapData = {
          id: `viator-product-marker-${item?.viatorActivity?.productCode}`,
          productCode: item?.viatorActivity?.productCode,
          day: item?.day ?? null,
          coordinates: coordinates,
          markerOffset: markerOffset,
        };

        return mapData;
      })
      .filter((item) => item !== null);

    const handleMarkerClick = (id) => {
      setActiveMarker(id);
      setShowPopup(true);

      const activityCardElement = document.getElementById(
        `viator-product-card-${id}`
      );
      if (activityCardElement) {
        activityCardElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    };

    // Generate map markers from map activities
    const mapMarkers = mapActivities.map((activity, index) => {
      const marker = (
        <MapMarker
          key={`${activity?.id ?? index}-${activity?.productCode}`}
          longitude={activity?.coordinates[0]}
          latitude={activity?.coordinates[1]}
          active={activeMarker === activity.productCode}
          onClick={() => handleMarkerClick(activity.productCode)}
          offset={activity?.markerOffset}
        />
      );
      return marker;
    });

    setCurrentMapMarkers(mapMarkers);
  }, [itineraryItems, activeMarker, viatorExperiences]);

  useEffect(() => {
    if (!activeMarker || !itineraryItems || !viatorExperiences || !inView) {
      return;
    }
    const newSelectedViatorProduct =
      [...Object.values(itineraryItems), ...viatorExperiences].find(
        (item) => item?.viatorActivity?.productCode === activeMarker
      )?.viatorActivity ?? null;

    setSelectedViatorProduct(newSelectedViatorProduct);
  }, [activeMarker, itineraryItems, viatorExperiences]);

  const west = location?.bounding_box?.[0];
  const east = location?.bounding_box?.[2];
  const boundingBoxCrosses180Meridan = west && east ? west > east : false;
  const zoomLevel = !boundingBoxCrosses180Meridan
    ? initialZoom
    : calculateZoomFromBoundingBox({ boundingBox: location?.bounding_box });

  return (
    <div className={`${styles.container}`} ref={observe}>
      {inView && (
        <Map
          mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
          initialViewState={{
            longitude: initialPosition?.lon ?? -122.4,
            latitude: initialPosition?.lat ?? 37.8,
            zoom: zoomLevel,
            bounds: !boundingBoxCrosses180Meridan
              ? location?.bounding_box
              : null,
            fitBoundsOptions: {
              maxZoom: 16,
              minZoom: 3,
            },
          }}
          style={{
            width: "100%",
            minHeight: "420px",
            height: "75vh",
            maxHeight: "680px",
          }}
          mapStyle="mapbox://styles/mapbox/streets-v8?optimize=true"
          attributionControl={false}
        >
          <GeolocateControl position="bottom-left" />
          <FullscreenControl position="top-left" />
          <NavigationControl position="bottom-left" />
          <AttributionControl position="top-right" />
          {/* <ScaleControl position="bottom-right" /> */}
          {currentMapMarkers && currentMapMarkers}
          {selectedViatorProduct && showPopup && (
            <ViatorPopup
              viatorActivity={selectedViatorProduct}
              onClose={() => {
                setShowPopup(false);
                setActiveMarker(null);
                setSelectedViatorProduct(null);
              }}
            />
          )}
        </Map>
      )}
    </div>
  );
}
